import { defineComponent, h, computed } from 'vue';
import { LegacyNegativeAnswerItem } from '@/ui/components/inspectionReport/legacyTypes';
import { useInspectionReport } from '@/ui/components/inspectionReport/useInspectioReport';
import { t, n } from '@/ui/i18n/i18n';
import TableBox from '@/ui/components/inspectionReport/items/table/TableBox.vue';
import { TableBoxHead } from '@/ui/components/inspectionReport/items/table/TableBoxHead';
import { isRoccScoringEnabled } from '@/ui/helpers/featureHelper';

interface OverviewProps {
  negativeAnswers: Array<LegacyNegativeAnswerItem>;
}

export const Overview = defineComponent<OverviewProps>({
  name: 'Overview',
  props: {
    negativeAnswers: {
      required: true,
      default: () => [],
    },
  },
  setup(props) {
    const { resource } = useInspectionReport();

    const template = computed(() => resource.value?.template);
    const templateScoreRange = computed(() => template.value?.score_range_labels ?? []);
    const displayScoreRange = computed(() => !!template.value?.display_score_range_labels);

    const renderScoreRangeElement = () => {
      if (!template.value || !displayScoreRange.value || templateScoreRange.value.length === 0) {
        return null;
      }

      const matchingRange = templateScoreRange.value.find((range, index) => {
        if (index === 0) {
          return resource.value?.percentage! >= range.min && resource.value?.percentage! <= range.max;
        }

        return resource.value?.percentage! > range.min && resource.value?.percentage! <= range.max;
      });

      return matchingRange
        ? h('div', {
          style: { color: matchingRange.color },
        },
        matchingRange.label)
        : null;
    };

    const boxSectionHeader = (label: string) => h(TableBoxHead, [label]);

    const hasRoccPassed = (value: boolean | undefined): boolean => value === true || value === undefined;

    const roccText = () => {
      const passed = hasRoccPassed(resource.value?.form.rocc_passing);

      return h('span', {
        class: passed ? 'has-text-success' : 'has-text-danger',
      }, [
        passed ? t('inspection-report.rocc.passing') : t('inspection-report.rocc.failing'),
      ]);
    };

    const scoreText = () => h('div', { class: 'flex items-end flex-wrap' }, [
      h('span', { class: 'mr-2' }, [
        resource.value?.form.max_score! > 0
          ? n(resource.value?.percentage! / 100, 'scorePercentage')
          : '-',
      ]),
      renderScoreRangeElement(),
    ]);

    return function render() {
      const headers: Array<ReturnType<typeof h> | string> = [];
      const items: Array<ReturnType<typeof h> | string> = [];

      if (template.value?.display_rocc_score && isRoccScoringEnabled()) {
        headers.push(boxSectionHeader(t('inspection-report.category-scoring-result')));
        items.push(roccText());
      }

      if (template.value?.display_score && resource.value?.form.max_score) {
        headers.push(boxSectionHeader(t('inspection-report.total-result')));
        items.push(scoreText());
      }

      if (template.value?.display_negative_answers && props.negativeAnswers.length) {
        headers.push(boxSectionHeader(t('inspection-report.negative-answers')));
        items.push(props.negativeAnswers.length.toString());
      }

      if (template.value?.display_issues && resource.value?.issues.length) {
        headers.push(boxSectionHeader(t('inspection-report.created-actions')));
        items.push(resource.value.issues.length.toString());
      }

      if (!items.length) {
        return null;
      }

      return h('table', { class: 'mb-4 inspection-report__table' }, [
        h('tbody', [
          h('tr', [headers]),
          h('tr', items.map(item => h(TableBox, [item]))),
        ]),
      ]);
    };
  },
});
